import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import secureStorage from '../utils/secureStorage';
import { toast } from 'sonner';
import {
  Heart, Plus, Calendar, Baby, Activity, Scale, Moon, Smile,
  Clock, Pill, FileText, Edit, Trash2, ChevronRight, AlertCircle,
  Stethoscope, Phone, Hospital, User, TrendingUp, Footprints,
  Camera, Image, Upload, X, ZoomIn
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { API } from '../App';

const PregnancyTracker = ({ user, setUser }) => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialog states
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [showLogWeight, setShowLogWeight] = useState(false);
  const [showLogSymptoms, setShowLogSymptoms] = useState(false);
  const [showKickCounter, setShowKickCounter] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showUploadScan, setShowUploadScan] = useState(false);
  const [showViewScan, setShowViewScan] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const fileInputRef = useRef(null);

  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '', date_of_birth: '', last_menstrual_period: '',
    blood_group: '', height_cm: '', pre_pregnancy_weight: '',
    medical_conditions: '', allergies: '',
    previous_pregnancies: 0, previous_births: 0, previous_miscarriages: 0,
    doctor_name: '', doctor_phone: '', hospital: '',
    emergency_contact_name: '', emergency_contact_phone: '', notes: ''
  });

  const [weightForm, setWeightForm] = useState({
    weight_kg: '', log_date: new Date().toISOString().split('T')[0], notes: ''
  });

  const [symptomForm, setSymptomForm] = useState({
    log_date: new Date().toISOString().split('T')[0],
    symptoms: [], severity: 'mild', mood: 'neutral',
    energy_level: 3, sleep_hours: '', sleep_quality: 3, notes: ''
  });

  const [kickForm, setKickForm] = useState({
    session_date: new Date().toISOString().split('T')[0],
    start_time: '', kick_count: 0, duration_minutes: '', notes: ''
  });

  const [appointmentForm, setAppointmentForm] = useState({
    appointment_date: '', appointment_time: '', appointment_type: 'checkup',
    doctor_name: '', location: '', notes: ''
  });

  const [medicationForm, setMedicationForm] = useState({
    medication_name: '', dosage: '', frequency: 'daily',
    start_date: '', prescribed_by: '', purpose: '', is_prenatal_vitamin: false, notes: ''
  });

  const [scanForm, setScanForm] = useState({
    scan_type: 'ultrasound',
    scan_date: new Date().toISOString().split('T')[0],
    week_number: '',
    notes: '',
    file: null
  });

  // Kick counter state
  const [isCountingKicks, setIsCountingKicks] = useState(false);
  const [kickCount, setKickCount] = useState(0);
  const [kickStartTime, setKickStartTime] = useState(null);

  const token = secureStorage.getToken();
  const headers = { Authorization: `Bearer ${token}` };

  const commonSymptoms = [
    'Nausea', 'Fatigue', 'Back pain', 'Headache', 'Heartburn',
    'Swelling', 'Leg cramps', 'Insomnia', 'Mood swings', 'Food cravings',
    'Frequent urination', 'Breast tenderness', 'Dizziness', 'Constipation'
  ];

  useEffect(() => {
    fetchAllProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProfile) {
      fetchProfileDashboard(selectedProfile.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfile]);

  const fetchAllProfiles = async () => {
    try {
      const response = await axios.get(`${API}/pregnancy/dashboard`, { headers });
      setProfiles(response.data.profiles || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      setLoading(false);
    }
  };

  const fetchProfileDashboard = async (profileId) => {
    try {
      const response = await axios.get(`${API}/pregnancy/dashboard/${profileId}`, { headers });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load pregnancy dashboard');
    }
  };

  const handleAddProfile = async () => {
    try {
      const data = {
        ...profileForm,
        height_cm: profileForm.height_cm ? parseFloat(profileForm.height_cm) : null,
        pre_pregnancy_weight: profileForm.pre_pregnancy_weight ? parseFloat(profileForm.pre_pregnancy_weight) : null,
        medical_conditions: profileForm.medical_conditions ? profileForm.medical_conditions.split(',').map(c => c.trim()) : [],
        allergies: profileForm.allergies ? profileForm.allergies.split(',').map(a => a.trim()) : []
      };

      if (editingProfile) {
        await axios.put(`${API}/pregnancy/profiles/${editingProfile.id}`, data, { headers });
        toast.success('Profile updated successfully');
      } else {
        await axios.post(`${API}/pregnancy/profiles`, data, { headers });
        toast.success('Pregnancy profile created');
      }

      setShowAddProfile(false);
      setEditingProfile(null);
      resetProfileForm();
      fetchAllProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving profile');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Delete this pregnancy profile and all associated data?')) return;
    try {
      await axios.delete(`${API}/pregnancy/profiles/${profileId}`, { headers });
      toast.success('Profile deleted');
      if (selectedProfile?.id === profileId) setSelectedProfile(null);
      fetchAllProfiles();
    } catch (error) {
      toast.error('Error deleting profile');
    }
  };

  const handleLogWeight = async () => {
    try {
      await axios.post(`${API}/pregnancy/weight`, {
        pregnancy_id: selectedProfile.id,
        ...weightForm,
        weight_kg: parseFloat(weightForm.weight_kg)
      }, { headers });
      toast.success('Weight logged');
      setShowLogWeight(false);
      setWeightForm({ weight_kg: '', log_date: new Date().toISOString().split('T')[0], notes: '' });
      fetchProfileDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error logging weight');
    }
  };

  const handleLogSymptoms = async () => {
    try {
      await axios.post(`${API}/pregnancy/symptoms`, {
        pregnancy_id: selectedProfile.id,
        ...symptomForm,
        sleep_hours: symptomForm.sleep_hours ? parseFloat(symptomForm.sleep_hours) : null
      }, { headers });
      toast.success('Symptoms logged');
      setShowLogSymptoms(false);
      setSymptomForm({
        log_date: new Date().toISOString().split('T')[0],
        symptoms: [], severity: 'mild', mood: 'neutral',
        energy_level: 3, sleep_hours: '', sleep_quality: 3, notes: ''
      });
      fetchProfileDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error logging symptoms');
    }
  };

  const startKickCounting = () => {
    setIsCountingKicks(true);
    setKickCount(0);
    setKickStartTime(new Date());
  };

  const recordKick = () => {
    setKickCount(prev => prev + 1);
  };

  const stopKickCounting = async () => {
    const endTime = new Date();
    const durationMinutes = Math.round((endTime - kickStartTime) / 60000);

    try {
      await axios.post(`${API}/pregnancy/kicks`, {
        pregnancy_id: selectedProfile.id,
        session_date: new Date().toISOString().split('T')[0],
        start_time: kickStartTime.toTimeString().slice(0, 5),
        end_time: endTime.toTimeString().slice(0, 5),
        kick_count: kickCount,
        duration_minutes: durationMinutes
      }, { headers });
      toast.success(`Recorded ${kickCount} kicks in ${durationMinutes} minutes`);
      setIsCountingKicks(false);
      setKickCount(0);
      setKickStartTime(null);
      fetchProfileDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error saving kick count');
    }
  };

  const handleAddAppointment = async () => {
    try {
      await axios.post(`${API}/pregnancy/appointments`, {
        pregnancy_id: selectedProfile.id,
        ...appointmentForm
      }, { headers });
      toast.success('Appointment added');
      setShowAddAppointment(false);
      setAppointmentForm({
        appointment_date: '', appointment_time: '', appointment_type: 'checkup',
        doctor_name: '', location: '', notes: ''
      });
      fetchProfileDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error adding appointment');
    }
  };

  const handleAddMedication = async () => {
    try {
      await axios.post(`${API}/pregnancy/medications`, {
        pregnancy_id: selectedProfile.id,
        ...medicationForm
      }, { headers });
      toast.success('Medication added');
      setShowAddMedication(false);
      setMedicationForm({
        medication_name: '', dosage: '', frequency: 'daily',
        start_date: '', prescribed_by: '', purpose: '', is_prenatal_vitamin: false, notes: ''
      });
      fetchProfileDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error adding medication');
    }
  };

  const handleUploadScan = async () => {
    if (!scanForm.file) {
      toast.error('Please select an image file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('pregnancy_id', selectedProfile.id);
      formData.append('scan_type', scanForm.scan_type);
      formData.append('scan_date', scanForm.scan_date);
      if (scanForm.week_number) formData.append('week_number', scanForm.week_number);
      if (scanForm.notes) formData.append('notes', scanForm.notes);
      formData.append('file', scanForm.file);

      await axios.post(`${API}/pregnancy/scans`, formData, {
        headers: { 
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Scan uploaded successfully');
      setShowUploadScan(false);
      setScanForm({
        scan_type: 'ultrasound',
        scan_date: new Date().toISOString().split('T')[0],
        week_number: '',
        notes: '',
        file: null
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchProfileDashboard(selectedProfile.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading scan');
    }
  };

  const handleViewScan = async (scan) => {
    try {
      const response = await axios.get(`${API}/pregnancy/scans/${selectedProfile.id}/${scan.id}`, { headers });
      setSelectedScan(response.data);
      setShowViewScan(true);
    } catch (error) {
      toast.error('Error loading scan');
    }
  };

  const handleDeleteScan = async (scanId) => {
    if (!window.confirm('Delete this scan image?')) return;
    try {
      await axios.delete(`${API}/pregnancy/scans/${selectedProfile.id}/${scanId}`, { headers });
      toast.success('Scan deleted');
      fetchProfileDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error deleting scan');
    }
  };

  const resetProfileForm = () => {
    setProfileForm({
      name: '', date_of_birth: '', last_menstrual_period: '',
      blood_group: '', height_cm: '', pre_pregnancy_weight: '',
      medical_conditions: '', allergies: '',
      previous_pregnancies: 0, previous_births: 0, previous_miscarriages: 0,
      doctor_name: '', doctor_phone: '', hospital: '',
      emergency_contact_name: '', emergency_contact_phone: '', notes: ''
    });
  };

  const getTrimesterColor = (trimester) => {
    const colors = {
      1: 'from-pink-500 to-rose-500',
      2: 'from-purple-500 to-indigo-500',
      3: 'from-blue-500 to-cyan-500'
    };
    return colors[trimester] || 'from-pink-500 to-rose-500';
  };

  const getMoodEmoji = (mood) => {
    const moods = { very_happy: '😄', happy: '🙂', neutral: '😐', sad: '😔', very_sad: '😢' };
    return moods[mood] || '😐';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <Navigation user={user} setUser={setUser} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-3">
              <Baby className="w-8 h-8 text-pink-600" />
              Pregnancy Tracker
            </h1>
            <p className="text-gray-600 mt-1">Track your pregnancy journey week by week</p>
          </div>

          <Button
            onClick={() => { setEditingProfile(null); resetProfileForm(); setShowAddProfile(true); }}
            className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 gap-2"
            data-testid="add-pregnancy-btn"
          >
            <Plus className="w-4 h-4" />
            New Pregnancy
          </Button>
        </div>

        {profiles.length === 0 ? (
          <Card className="text-center py-16" data-testid="empty-state">
            <CardContent>
              <Baby className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Pregnancy Profiles</h3>
              <p className="text-gray-500 mb-6">Start tracking your pregnancy journey</p>
              <Button onClick={() => setShowAddProfile(true)} className="bg-pink-600 hover:bg-pink-700">
                <Plus className="w-4 h-4 mr-2" /> Add Pregnancy
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Profiles Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Heart className="w-5 h-5 text-pink-600" />
                    Pregnancies
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {profiles.map((profile) => (
                    <div
                      key={profile.id}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedProfile?.id === profile.id
                          ? 'bg-pink-50 border-2 border-pink-200'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      onClick={() => setSelectedProfile(profile)}
                      data-testid={`profile-${profile.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getTrimesterColor(profile.trimester)} flex items-center justify-center text-white font-bold`}>
                            {profile.current_week || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{profile.name}</p>
                            <p className="text-xs text-gray-500">
                              Week {profile.current_week || '?'} • T{profile.trimester || '?'}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {!selectedProfile ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Baby className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Select a pregnancy to view details</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Profile Header */}
                  <Card className={`bg-gradient-to-r ${getTrimesterColor(dashboard?.progress?.trimester || 1)} text-white`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="text-3xl font-bold">{dashboard?.progress?.weeks || '?'}</span>
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold">{selectedProfile.name}</h2>
                            <p className="text-white/80">
                              Week {dashboard?.progress?.weeks || '?'}, Day {dashboard?.progress?.days || 0}
                            </p>
                            <p className="text-white/70 text-sm">
                              Trimester {dashboard?.progress?.trimester || '?'} • Due: {dashboard?.progress?.due_date || 'TBD'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" className="text-white hover:bg-white/20" onClick={() => {
                            setEditingProfile(selectedProfile);
                            setProfileForm({
                              ...selectedProfile,
                              medical_conditions: selectedProfile.medical_conditions?.join(', ') || '',
                              allergies: selectedProfile.allergies?.join(', ') || ''
                            });
                            setShowAddProfile(true);
                          }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-6">
                        <div className="flex justify-between text-sm text-white/80 mb-2">
                          <span>Progress</span>
                          <span>{dashboard?.progress?.progress_percent || 0}%</span>
                        </div>
                        <Progress value={dashboard?.progress?.progress_percent || 0} className="h-3 bg-white/20" />
                        <p className="text-sm text-white/70 mt-2">
                          {dashboard?.progress?.days_remaining || '?'} days remaining
                        </p>
                      </div>

                      {/* Weekly Milestone */}
                      {dashboard?.milestone && (
                        <div className="mt-4 p-3 bg-white/10 rounded-lg">
                          <p className="text-sm font-medium">This week your baby is the size of a {dashboard?.milestone?.size}</p>
                          <p className="text-xs text-white/70 mt-1">{dashboard?.milestone?.development}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowLogWeight(true)}>
                      <Scale className="w-5 h-5 text-pink-600" />
                      <span className="text-xs">Log Weight</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowLogSymptoms(true)}>
                      <Activity className="w-5 h-5 text-purple-600" />
                      <span className="text-xs">Log Symptoms</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowKickCounter(true)}>
                      <Footprints className="w-5 h-5 text-blue-600" />
                      <span className="text-xs">Kick Counter</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowUploadScan(true)}>
                      <Camera className="w-5 h-5 text-rose-600" />
                      <span className="text-xs">Upload Scan</span>
                    </Button>
                  </div>

                  {/* Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-5 gap-2">
                      <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
                      <TabsTrigger value="health" className="text-xs sm:text-sm">Health</TabsTrigger>
                      <TabsTrigger value="scans" className="text-xs sm:text-sm">Scans</TabsTrigger>
                      <TabsTrigger value="appointments" className="text-xs sm:text-sm">Appointments</TabsTrigger>
                      <TabsTrigger value="medications" className="text-xs sm:text-sm">Meds</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-4 space-y-4">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-pink-50 border-pink-100">
                          <CardContent className="p-4 text-center">
                            <Scale className="w-6 h-6 text-pink-600 mx-auto mb-2" />
                            <p className="text-lg font-bold text-pink-700">
                              {dashboard?.latest_weight?.weight_kg || '-'} kg
                            </p>
                            <p className="text-xs text-pink-600">Current Weight</p>
                            {dashboard?.weight_gain_kg != null && (
                              <p className="text-xs text-gray-500 mt-1">
                                +{dashboard?.weight_gain_kg} kg gain
                              </p>
                            )}
                          </CardContent>
                        </Card>
                        <Card className="bg-purple-50 border-purple-100">
                          <CardContent className="p-4 text-center">
                            <Calendar className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                            <p className="text-lg font-bold text-purple-700">
                              {dashboard?.upcoming_appointments?.length || 0}
                            </p>
                            <p className="text-xs text-purple-600">Upcoming Visits</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-blue-50 border-blue-100">
                          <CardContent className="p-4 text-center">
                            <Footprints className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="text-lg font-bold text-blue-700">
                              {dashboard?.recent_kicks?.[0]?.kick_count || '-'}
                            </p>
                            <p className="text-xs text-blue-600">Last Kick Count</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-green-50 border-green-100">
                          <CardContent className="p-4 text-center">
                            <Pill className="w-6 h-6 text-green-600 mx-auto mb-2" />
                            <p className="text-lg font-bold text-green-700">
                              {dashboard?.medications_count || 0}
                            </p>
                            <p className="text-xs text-green-600">Active Medications</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Upcoming Appointments */}
                      {dashboard?.upcoming_appointments?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg">Upcoming Appointments</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {dashboard?.upcoming_appointments?.map((apt, idx) => (
                                <div key={`item-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <Calendar className="w-5 h-5 text-pink-500" />
                                    <div>
                                      <p className="font-medium capitalize">{apt.appointment_type?.replace('_', ' ')}</p>
                                      <p className="text-sm text-gray-500">
                                        {apt.appointment_date} at {apt.appointment_time}
                                      </p>
                                    </div>
                                  </div>
                                  {apt.location && <Badge variant="outline">{apt.location}</Badge>}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Recent Symptoms */}
                      {dashboard?.recent_symptoms?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg">Recent Symptoms</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {dashboard?.recent_symptoms?.slice(0, 3).map((log, idx) => (
                                <div key={`item-${idx}`} className="p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-500">{log.log_date}</span>
                                    <span className="text-lg">{getMoodEmoji(log.mood)}</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {log.symptoms?.map((s, i) => (
                                      <Badge key={`item-${i}`} variant="outline" className="text-xs">{s}</Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Doctor Info */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Stethoscope className="w-5 h-5 text-pink-500" />
                            Healthcare Provider
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-3 bg-pink-50 rounded-lg">
                              <p className="font-medium">{selectedProfile.doctor_name || 'Not specified'}</p>
                              <p className="text-sm text-gray-600">{selectedProfile.doctor_phone || '-'}</p>
                              {selectedProfile.hospital && (
                                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                                  <Hospital className="w-3 h-3" /> {selectedProfile.hospital}
                                </p>
                              )}
                            </div>
                            <div className="p-3 bg-red-50 rounded-lg">
                              <p className="text-sm text-gray-500">Emergency Contact</p>
                              <p className="font-medium">{selectedProfile.emergency_contact_name || 'Not specified'}</p>
                              <p className="text-sm text-gray-600">{selectedProfile.emergency_contact_phone || '-'}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="health" className="mt-4 space-y-4">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" onClick={() => setShowLogWeight(true)} className="bg-pink-600 hover:bg-pink-700">
                          <Scale className="w-4 h-4 mr-1" /> Log Weight
                        </Button>
                        <Button size="sm" onClick={() => setShowLogSymptoms(true)} className="bg-purple-600 hover:bg-purple-700">
                          <Activity className="w-4 h-4 mr-1" /> Log Symptoms
                        </Button>
                      </div>

                      {/* Weight Chart Placeholder */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Weight Tracking</CardTitle>
                          <CardDescription>
                            Pre-pregnancy: {selectedProfile.pre_pregnancy_weight || '-'} kg • 
                            Current: {dashboard?.latest_weight?.weight_kg || '-'} kg •
                            Gain: {dashboard?.weight_gain_kg != null ? `+${dashboard?.weight_gain_kg} kg` : '-'}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-500 text-center py-4">Weight history chart coming soon</p>
                        </CardContent>
                      </Card>

                      {/* Kick Counter */}
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <div>
                            <CardTitle>Kick Counter</CardTitle>
                            <CardDescription>Track baby's movements</CardDescription>
                          </div>
                          <Button size="sm" onClick={() => setShowKickCounter(true)}>
                            <Footprints className="w-4 h-4 mr-1" /> Start Counting
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {dashboard?.recent_kicks?.length > 0 ? (
                            <div className="space-y-2">
                              {dashboard?.recent_kicks?.slice(0, 5).map((kick, idx) => (
                                <div key={`item-${idx}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <span className="text-sm">{kick.session_date}</span>
                                  <span className="font-medium">{kick.kick_count} kicks</span>
                                  <span className="text-sm text-gray-500">{kick.duration_minutes} min</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No kick sessions recorded yet</p>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="scans" className="mt-4 space-y-4">
                      <div className="flex justify-end">
                        <Button size="sm" onClick={() => setShowUploadScan(true)} className="bg-pink-600 hover:bg-pink-700">
                          <Upload className="w-4 h-4 mr-1" /> Upload Scan
                        </Button>
                      </div>

                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Image className="w-5 h-5 text-pink-500" />
                            Baby Scan Images
                          </CardTitle>
                          <CardDescription>Ultrasound and scan images from your pregnancy journey</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {dashboard?.scans?.length === 0 ? (
                            <div className="text-center py-8">
                              <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500">No scan images uploaded yet</p>
                              <p className="text-sm text-gray-400 mt-1">Upload your ultrasound images to keep memories of your journey</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {dashboard?.scans?.map((scan, idx) => (
                                <div key={`item-${idx}`} className="relative group border rounded-lg overflow-hidden bg-gray-50">
                                  <div 
                                    className="aspect-square flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => handleViewScan(scan)}
                                  >
                                    <div className="text-center p-4">
                                      <Image className="w-10 h-10 text-pink-400 mx-auto mb-2" />
                                      <p className="text-xs font-medium text-gray-700 capitalize">{scan.scan_type}</p>
                                      <p className="text-xs text-gray-500">{scan.scan_date}</p>
                                      {scan.week_number && (
                                        <Badge variant="outline" className="mt-1 text-xs">Week {scan.week_number}</Badge>
                                      )}
                                    </div>
                                  </div>
                                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <Button size="sm" variant="secondary" className="h-7 w-7 p-0" onClick={() => handleViewScan(scan)}>
                                      <ZoomIn className="w-3 h-3" />
                                    </Button>
                                    <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => handleDeleteScan(scan.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="appointments" className="mt-4 space-y-4">
                      <div className="flex justify-end">
                        <Button size="sm" onClick={() => setShowAddAppointment(true)} className="bg-pink-600 hover:bg-pink-700">
                          <Plus className="w-4 h-4 mr-1" /> Add Appointment
                        </Button>
                      </div>

                      <Card>
                        <CardHeader>
                          <CardTitle>Prenatal Appointments</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {dashboard?.upcoming_appointments?.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No appointments scheduled</p>
                          ) : (
                            <div className="space-y-3">
                              {dashboard?.upcoming_appointments?.map((apt, idx) => (
                                <div key={`item-${idx}`} className="p-4 border rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <Badge className="capitalize mb-2">{apt.appointment_type.replace('_', ' ')}</Badge>
                                      <p className="font-medium">{apt.appointment_date} at {apt.appointment_time}</p>
                                      {apt.doctor_name && <p className="text-sm text-gray-600">Dr. {apt.doctor_name}</p>}
                                      {apt.location && <p className="text-sm text-gray-500">{apt.location}</p>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="medications" className="mt-4 space-y-4">
                      <div className="flex justify-end">
                        <Button size="sm" onClick={() => setShowAddMedication(true)} className="bg-pink-600 hover:bg-pink-700">
                          <Plus className="w-4 h-4 mr-1" /> Add Medication
                        </Button>
                      </div>

                      <Card>
                        <CardHeader>
                          <CardTitle>Medications & Supplements</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {dashboard?.active_medications?.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No medications added</p>
                          ) : (
                            <div className="space-y-3">
                              {dashboard?.active_medications?.map((med, idx) => (
                                <div key={`item-${idx}`} className={`p-4 border rounded-lg ${med.is_prenatal_vitamin ? 'bg-green-50 border-green-200' : ''}`}>
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium flex items-center gap-2">
                                        {med.medication_name}
                                        {med.is_prenatal_vitamin && <Badge className="bg-green-500">Prenatal</Badge>}
                                      </p>
                                      <p className="text-sm text-gray-600">{med.dosage} • {med.frequency}</p>
                                      {med.purpose && <p className="text-sm text-gray-500">{med.purpose}</p>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        )}

        {/* Add/Edit Profile Dialog */}
        <Dialog open={showAddProfile} onOpenChange={setShowAddProfile}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProfile ? 'Edit Pregnancy' : 'New Pregnancy'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Your Name *</Label>
                  <Input value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} />
                </div>
                <div>
                  <Label>Date of Birth *</Label>
                  <Input type="date" value={profileForm.date_of_birth} onChange={(e) => setProfileForm({...profileForm, date_of_birth: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Last Menstrual Period (LMP) *</Label>
                  <Input type="date" value={profileForm.last_menstrual_period} onChange={(e) => setProfileForm({...profileForm, last_menstrual_period: e.target.value})} />
                </div>
                <div>
                  <Label>Blood Group</Label>
                  <Select value={profileForm.blood_group || ''} onValueChange={(v) => setProfileForm({...profileForm, blood_group: v})}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Height (cm)</Label>
                  <Input type="number" value={profileForm.height_cm} onChange={(e) => setProfileForm({...profileForm, height_cm: e.target.value})} />
                </div>
                <div>
                  <Label>Pre-pregnancy Weight (kg)</Label>
                  <Input type="number" value={profileForm.pre_pregnancy_weight} onChange={(e) => setProfileForm({...profileForm, pre_pregnancy_weight: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Medical Conditions (comma separated)</Label>
                <Input value={profileForm.medical_conditions} onChange={(e) => setProfileForm({...profileForm, medical_conditions: e.target.value})} placeholder="Diabetes, Hypertension" />
              </div>
              <div>
                <Label>Allergies (comma separated)</Label>
                <Input value={profileForm.allergies} onChange={(e) => setProfileForm({...profileForm, allergies: e.target.value})} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Previous Pregnancies</Label>
                  <Input type="number" min="0" value={profileForm.previous_pregnancies} onChange={(e) => setProfileForm({...profileForm, previous_pregnancies: parseInt(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Previous Births</Label>
                  <Input type="number" min="0" value={profileForm.previous_births} onChange={(e) => setProfileForm({...profileForm, previous_births: parseInt(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Previous Miscarriages</Label>
                  <Input type="number" min="0" value={profileForm.previous_miscarriages} onChange={(e) => setProfileForm({...profileForm, previous_miscarriages: parseInt(e.target.value) || 0})} />
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="font-medium text-gray-700 mb-3">Healthcare Provider</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Doctor Name</Label>
                    <Input value={profileForm.doctor_name || ''} onChange={(e) => setProfileForm({...profileForm, doctor_name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Doctor Phone</Label>
                    <Input value={profileForm.doctor_phone || ''} onChange={(e) => setProfileForm({...profileForm, doctor_phone: e.target.value})} />
                  </div>
                </div>
                <div className="mt-2">
                  <Label>Hospital/Clinic</Label>
                  <Input value={profileForm.hospital || ''} onChange={(e) => setProfileForm({...profileForm, hospital: e.target.value})} />
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="font-medium text-gray-700 mb-3">Emergency Contact</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={profileForm.emergency_contact_name || ''} onChange={(e) => setProfileForm({...profileForm, emergency_contact_name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={profileForm.emergency_contact_phone || ''} onChange={(e) => setProfileForm({...profileForm, emergency_contact_phone: e.target.value})} />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddProfile(false)}>Cancel</Button>
              <Button onClick={handleAddProfile} className="bg-pink-600 hover:bg-pink-700">{editingProfile ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Log Weight Dialog */}
        <Dialog open={showLogWeight} onOpenChange={setShowLogWeight}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Log Weight</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={weightForm.log_date} onChange={(e) => setWeightForm({...weightForm, log_date: e.target.value})} />
              </div>
              <div>
                <Label>Weight (kg) *</Label>
                <Input type="number" step="0.1" value={weightForm.weight_kg} onChange={(e) => setWeightForm({...weightForm, weight_kg: e.target.value})} placeholder="65.5" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={weightForm.notes} onChange={(e) => setWeightForm({...weightForm, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLogWeight(false)}>Cancel</Button>
              <Button onClick={handleLogWeight} className="bg-pink-600 hover:bg-pink-700">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Log Symptoms Dialog */}
        <Dialog open={showLogSymptoms} onOpenChange={setShowLogSymptoms}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Log Symptoms</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={symptomForm.log_date} onChange={(e) => setSymptomForm({...symptomForm, log_date: e.target.value})} />
              </div>
              <div>
                <Label>Symptoms</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {commonSymptoms.map(symptom => (
                    <Badge
                      key={symptom}
                      variant={symptomForm.symptoms.includes(symptom) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        setSymptomForm(prev => ({
                          ...prev,
                          symptoms: prev.symptoms.includes(symptom)
                            ? prev.symptoms.filter(s => s !== symptom)
                            : [...prev.symptoms, symptom]
                        }));
                      }}
                    >
                      {symptom}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Severity</Label>
                  <Select value={symptomForm.severity} onValueChange={(v) => setSymptomForm({...symptomForm, severity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mild">Mild</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="severe">Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mood</Label>
                  <Select value={symptomForm.mood} onValueChange={(v) => setSymptomForm({...symptomForm, mood: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="very_happy">😄 Very Happy</SelectItem>
                      <SelectItem value="happy">🙂 Happy</SelectItem>
                      <SelectItem value="neutral">😐 Neutral</SelectItem>
                      <SelectItem value="sad">😔 Sad</SelectItem>
                      <SelectItem value="very_sad">😢 Very Sad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sleep Hours</Label>
                  <Input type="number" step="0.5" value={symptomForm.sleep_hours} onChange={(e) => setSymptomForm({...symptomForm, sleep_hours: e.target.value})} />
                </div>
                <div>
                  <Label>Energy Level (1-5)</Label>
                  <Input type="number" min="1" max="5" value={symptomForm.energy_level} onChange={(e) => setSymptomForm({...symptomForm, energy_level: parseInt(e.target.value) || 3})} />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={symptomForm.notes} onChange={(e) => setSymptomForm({...symptomForm, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLogSymptoms(false)}>Cancel</Button>
              <Button onClick={handleLogSymptoms} className="bg-purple-600 hover:bg-purple-700">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Kick Counter Dialog */}
        <Dialog open={showKickCounter} onOpenChange={setShowKickCounter}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Kick Counter</DialogTitle>
            </DialogHeader>
            <div className="text-center py-8">
              {!isCountingKicks ? (
                <div>
                  <Footprints className="w-16 h-16 text-pink-500 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Tap the button each time you feel baby move</p>
                  <Button onClick={startKickCounting} className="bg-pink-600 hover:bg-pink-700" size="lg">
                    Start Counting
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="text-6xl font-bold text-pink-600 mb-4">{kickCount}</div>
                  <p className="text-gray-600 mb-4">kicks counted</p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={recordKick}
                      className="w-32 h-32 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-xl"
                    >
                      <Footprints className="w-12 h-12" />
                    </Button>
                  </div>
                  <Button variant="outline" onClick={stopKickCounting} className="mt-6">
                    Stop & Save
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Appointment Dialog */}
        <Dialog open={showAddAppointment} onOpenChange={setShowAddAppointment}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Appointment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={appointmentForm.appointment_date} onChange={(e) => setAppointmentForm({...appointmentForm, appointment_date: e.target.value})} />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input type="time" value={appointmentForm.appointment_time} onChange={(e) => setAppointmentForm({...appointmentForm, appointment_time: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={appointmentForm.appointment_type} onValueChange={(v) => setAppointmentForm({...appointmentForm, appointment_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checkup">Regular Checkup</SelectItem>
                    <SelectItem value="ultrasound">Ultrasound</SelectItem>
                    <SelectItem value="lab_test">Lab Test</SelectItem>
                    <SelectItem value="specialist">Specialist Visit</SelectItem>
                    <SelectItem value="delivery_prep">Delivery Preparation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Doctor Name</Label>
                <Input value={appointmentForm.doctor_name} onChange={(e) => setAppointmentForm({...appointmentForm, doctor_name: e.target.value})} />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={appointmentForm.location} onChange={(e) => setAppointmentForm({...appointmentForm, location: e.target.value})} />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={appointmentForm.notes} onChange={(e) => setAppointmentForm({...appointmentForm, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddAppointment(false)}>Cancel</Button>
              <Button onClick={handleAddAppointment} className="bg-pink-600 hover:bg-pink-700">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Medication Dialog */}
        <Dialog open={showAddMedication} onOpenChange={setShowAddMedication}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Medication</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Medication Name *</Label>
                <Input value={medicationForm.medication_name} onChange={(e) => setMedicationForm({...medicationForm, medication_name: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Dosage *</Label>
                  <Input value={medicationForm.dosage} onChange={(e) => setMedicationForm({...medicationForm, dosage: e.target.value})} placeholder="500mg" />
                </div>
                <div>
                  <Label>Frequency *</Label>
                  <Select value={medicationForm.frequency} onValueChange={(v) => setMedicationForm({...medicationForm, frequency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="twice_daily">Twice Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="as_needed">As Needed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={medicationForm.start_date} onChange={(e) => setMedicationForm({...medicationForm, start_date: e.target.value})} />
              </div>
              <div>
                <Label>Purpose</Label>
                <Input value={medicationForm.purpose} onChange={(e) => setMedicationForm({...medicationForm, purpose: e.target.value})} />
              </div>
              <div>
                <Label>Prescribed By</Label>
                <Input value={medicationForm.prescribed_by} onChange={(e) => setMedicationForm({...medicationForm, prescribed_by: e.target.value})} />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={medicationForm.is_prenatal_vitamin}
                  onChange={(e) => setMedicationForm({...medicationForm, is_prenatal_vitamin: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">This is a prenatal vitamin</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddMedication(false)}>Cancel</Button>
              <Button onClick={handleAddMedication} className="bg-pink-600 hover:bg-pink-700">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Scan Dialog */}
        <Dialog open={showUploadScan} onOpenChange={setShowUploadScan}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-pink-500" />
                Upload Baby Scan
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Scan Type *</Label>
                <Select value={scanForm.scan_type} onValueChange={(v) => setScanForm({...scanForm, scan_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ultrasound">Ultrasound</SelectItem>
                    <SelectItem value="3d_scan">3D Scan</SelectItem>
                    <SelectItem value="4d_scan">4D Scan</SelectItem>
                    <SelectItem value="anomaly_scan">Anomaly Scan</SelectItem>
                    <SelectItem value="growth_scan">Growth Scan</SelectItem>
                    <SelectItem value="doppler">Doppler Scan</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Scan Date *</Label>
                  <Input 
                    type="date" 
                    value={scanForm.scan_date} 
                    onChange={(e) => setScanForm({...scanForm, scan_date: e.target.value})} 
                  />
                </div>
                <div>
                  <Label>Week Number</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    max="42"
                    placeholder="e.g., 20"
                    value={scanForm.week_number} 
                    onChange={(e) => setScanForm({...scanForm, week_number: e.target.value})} 
                  />
                </div>
              </div>
              <div>
                <Label>Scan Image *</Label>
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    onChange={(e) => setScanForm({...scanForm, file: e.target.files[0]})}
                    className="hidden"
                    id="scan-file-input"
                  />
                  <label 
                    htmlFor="scan-file-input"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    {scanForm.file ? (
                      <div className="text-center">
                        <Image className="w-8 h-8 text-pink-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-700">{scanForm.file.name}</p>
                        <p className="text-xs text-gray-500">{(scanForm.file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Click to upload scan image</p>
                        <p className="text-xs text-gray-400">JPEG, PNG, WebP up to 10MB</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={scanForm.notes} 
                  onChange={(e) => setScanForm({...scanForm, notes: e.target.value})} 
                  placeholder="Any notes about this scan..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadScan(false)}>Cancel</Button>
              <Button onClick={handleUploadScan} className="bg-pink-600 hover:bg-pink-700">
                <Upload className="w-4 h-4 mr-1" /> Upload
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Scan Dialog */}
        <Dialog open={showViewScan} onOpenChange={setShowViewScan}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 capitalize">
                <Image className="w-5 h-5 text-pink-500" />
                {selectedScan?.scan_type?.replace('_', ' ')} - {selectedScan?.scan_date}
              </DialogTitle>
            </DialogHeader>
            {selectedScan && (
              <div className="space-y-4">
                <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                  {selectedScan.image_data ? (
                    <img 
                      src={`data:${selectedScan.content_type};base64,${selectedScan.image_data}`}
                      alt={`${selectedScan.scan_type} scan`}
                      className="max-h-[60vh] object-contain rounded"
                    />
                  ) : (
                    <div className="text-center py-12">
                      <Image className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Image loading...</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  <Badge variant="outline" className="capitalize">{selectedScan.scan_type?.replace('_', ' ')}</Badge>
                  {selectedScan.week_number && <Badge variant="outline">Week {selectedScan.week_number}</Badge>}
                  <Badge variant="outline">{selectedScan.scan_date}</Badge>
                </div>
                {selectedScan.notes && (
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedScan.notes}</p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewScan(false)}>Close</Button>
              <Button 
                variant="destructive" 
                onClick={() => {
                  handleDeleteScan(selectedScan.id);
                  setShowViewScan(false);
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default PregnancyTracker;
